// src/components/user/UsersSearchPanel.tsx
//
// UsersSearchPanel
// - Presentation-only search UI for Users page.
// - Delegates all state ownership to the parent page.
// - Uses the shared SearchPanel container for:
//   - Search / Reset actions
//   - Optional auto-search (debounced) controlled by the page via `autoSearch` prop
//   - Active filter chips (removable, with readable labels)
//
// IMPORTANT:
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel has global defaults via ./constants.
//   This component should only pass through `autoSearch` if provided by the page.

import React, { useMemo } from 'react';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import {
  SearchPanel,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SetSearchValueFn,
} from '@components/common/search';

import { UserGroup, UserStatus } from '@models/user';

type Option<T> = { label: string; value: T; description?: string };

export type UsersSearchForm = {
  username?: string;
  name?: string;
  group?: UserGroup;
  status?: UserStatus;
};

export type UsersSearchPanelProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;

  // Draft search state (owned by page)
  draftSearch: UsersSearchForm;
  setValue: SetSearchValueFn<UsersSearchForm>;

  // Options + selected values (owned by page)
  groupOptions: Option<UserGroup>[];
  statusOptions: Option<UserStatus>[];
  selectedGroup: Option<UserGroup> | null;
  selectedStatus: Option<UserStatus> | null;

  // Actions (owned by page)
  onSearch: () => void;
  onReset: () => void;

  // Optional: disable buttons when needed
  searchDisabled?: boolean;
  resetDisabled?: boolean;

  /**
   * Auto-search configuration provided by the page.
   * If omitted, SearchPanel falls back to its global defaults.
   */
  autoSearch?: { enabled: boolean; delayMs?: number };
};

const UsersSearchPanel: React.FC<UsersSearchPanelProps> = ({
  open,
  onOpenChange,

  draftSearch,
  setValue,

  groupOptions,
  statusOptions,
  selectedGroup,
  selectedStatus,

  onSearch,
  onReset,

  searchDisabled = false,
  resetDisabled = false,

  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Lookup maps (O(1) chip label access)                                */
  /* ------------------------------------------------------------------ */

  const groupLabelByValue = useMemo(() => {
    const map = new Map<UserGroup, string>();
    groupOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [groupOptions]);

  const statusLabelByValue = useMemo(() => {
    const map = new Map<UserStatus, string>();
    statusOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [statusOptions]);

  /* ------------------------------------------------------------------ */
  /* Active chips configuration                                          */
  /* ------------------------------------------------------------------ */

  const chips: SearchChipSpec<UsersSearchForm>[] = useMemo(
    () => [
      {
        key: 'username',
        label: 'ID',
        getValueText: (s) => String(s.username ?? '').trim(),
        clear: ({ setValue }) => setValue('username', null),
      },
      {
        key: 'name',
        label: 'Name',
        getValueText: (s) => String(s.name ?? '').trim(),
        clear: ({ setValue }) => setValue('name', null),
      },
      {
        key: 'group',
        label: 'Role',
        isActive: (s) => Boolean(s.group),
        getValueText: (s) => {
          const v = s.group;
          if (!v) return '';
          return groupLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('group', null),
      },
      {
        key: 'status',
        label: 'Status',
        isActive: (s) => Boolean(s.status),
        getValueText: (s) => {
          const v = s.status;
          if (!v) return '';
          return statusLabelByValue.get(v) ?? String(v);
        },
        clear: ({ setValue }) => setValue('status', null),
      },
    ],
    [groupLabelByValue, statusLabelByValue],
  );

  const chipsConfig: ActiveChipsConfig<UsersSearchForm> = useMemo(
    () => ({
      enabled: true,
      search: draftSearch,
      setValue,
      chips,
      maxVisible: 2,
      // Keep behavior consistent with other pages:
      // chip removal should immediately run a search.
      onAfterClear: onSearch,
    }),
    [chips, draftSearch, onSearch, setValue],
  );

  // ✅ Only pass autoSearch when explicitly enabled.
  // If omitted (or enabled !== true), SearchPanel uses its global defaults.
  const panelAutoSearch = autoSearch?.enabled === true ? autoSearch : undefined;

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      searchDisabled={searchDisabled}
      resetDisabled={resetDisabled}
      chipsConfig={chipsConfig}
      autoSearch={panelAutoSearch}
    >
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ minWidth: 0 }}>
        <SearchTextField
          label="ID"
          placeholder="Enter user ID"
          size="small"
          value={draftSearch.username ?? ''}
          onChangeValue={(v) => setValue('username', v)}
          onClear={() => setValue('username', null)}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />

        <SearchTextField
          label="Name"
          placeholder="Enter user name"
          size="small"
          value={draftSearch.name ?? ''}
          onChangeValue={(v) => setValue('name', v)}
          onClear={() => setValue('name', null)}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />

        <Autocomplete<Option<UserGroup>, false, false, false>
          disablePortal
          options={groupOptions}
          value={selectedGroup}
          onChange={(_e, opt) => setValue('group', (opt?.value ?? null) as UserGroup | null)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderInput={(params) => <TextField {...params} label="Role" size="small" placeholder="Select role" />}
          renderOption={(props, opt) => (
            <li {...props} key={String(opt.value)}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" fontWeight={800}>
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.description || ' '}
                </Typography>
              </Box>
            </li>
          )}
          sx={{ minWidth: 260, flex: '1 1 260px' }}
          clearOnEscape
        />

        <Autocomplete<Option<UserStatus>, false, false, false>
          disablePortal
          options={statusOptions}
          value={selectedStatus}
          onChange={(_e, opt) => setValue('status', (opt?.value ?? null) as UserStatus | null)}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          renderInput={(params) => <TextField {...params} label="Status" size="small" placeholder="Select status" />}
          renderOption={(props, opt) => (
            <li {...props} key={String(opt.value)}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" fontWeight={800}>
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.description || ' '}
                </Typography>
              </Box>
            </li>
          )}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
          clearOnEscape
        />
      </Stack>
    </SearchPanel>
  );
};

export default UsersSearchPanel;
